import { ChangeEvent, useState, useMemo, useCallback, useEffect } from 'react';
import { ColumnState as ColumnStateBase } from 'ag-grid-community';
import { ColumnsBase as Columns, IColumnState, IColumnsProps } from '@glu/data-components';
import { useComposedGridContext } from 'ComposedGrid/ComposedGridContext';

interface IUseColumnsAgGridProps
  extends Pick<IColumnsProps, 'disablePinning' | 'autoApplyChanges'> {
  /** Callback to close the drawer */
  onClose(): void;
}

export interface IColumnsAgGridProps extends Partial<IColumnsProps>, IUseColumnsAgGridProps {}

export const useColumnsAgGrid = ({
  autoApplyChanges,
  disablePinning,
  onClose,
}: IUseColumnsAgGridProps) => {
  const { gridRef } = useComposedGridContext();
  const addAttributes = useCallback(
    (colState: ColumnStateBase[]) =>
      colState.reduce<IColumnState[]>((drawerColumns, column) => {
        const currColumn = gridRef.current.columnApi.getColumn(column.colId)!;
        const currColumnDef = currColumn.getColDef();
        if (currColumnDef.showDisabledCheckboxes) {
          return drawerColumns;
        }
        return [
          ...drawerColumns,
          {
            ...column,
            id: column.colId,
            headerName: gridRef.current.columnApi.getDisplayNameForColumn(currColumn, null),
            primary: currColumnDef.primary,
            lockPosition: currColumnDef.lockPosition,
          },
        ];
      }, []),
    [gridRef]
  );
  const columnState = addAttributes(gridRef.current.columnApi.getColumnState());
  const [localColumnState, setLocalColumnState] = useState(columnState);
  const [filterValue, setFilterValue] = useState('');
  const onFilterChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setFilterValue(newValue);
  }, []);

  const filteredColumns = useMemo(() => {
    if (!filterValue) return localColumnState;
    const regex = new RegExp(filterValue, 'gi');
    return localColumnState.filter((column) => column.headerName.match(regex));
  }, [filterValue, localColumnState]);

  const showUnpinAll = useMemo(
    () => localColumnState.filter((column) => column.pinned).length > 0 && !disablePinning,
    [localColumnState, disablePinning]
  );

  const showAll = useMemo(
    () => localColumnState.filter((column) => column.hide).length === 0,
    [localColumnState]
  );

  const onApply = useCallback(() => {
    gridRef.current.columnApi.applyColumnState({ state: localColumnState, applyOrder: true });
    onClose();
  }, [gridRef, localColumnState, onClose]);

  const onCancel = useCallback(() => {
    setLocalColumnState(columnState);
    onClose();
  }, [columnState, onClose]);

  const onMove = useCallback((id: IColumnState['colId']) => {
    setLocalColumnState((currLocalColumnState) =>
      currLocalColumnState.map((column) => {
        if (column.colId === id) {
          return { ...column, primary: !column.primary };
        }
        return column;
      })
    );
  }, []);

  const onMoveEnd = useCallback((newColState: IColumnState[]) => {
    setLocalColumnState(newColState);
  }, []);

  const onPin = useCallback((id: IColumnState['colId'], direction: IColumnState['pinned']) => {
    setLocalColumnState((currLocalColumnState) =>
      currLocalColumnState.map((column) => {
        if (column.colId === id) {
          return { ...column, pinned: direction };
        }
        return column;
      })
    );
  }, []);

  const onUnpinAll = useCallback(() => {
    setLocalColumnState((l) => l.map((c) => ({ ...c, pinned: null })));
  }, []);

  const onShow = useCallback((field: IColumnState['colId']) => {
    setLocalColumnState((currLocalColumnState) =>
      currLocalColumnState.map((column) => {
        if (column.colId === field) {
          return { ...column, hide: !column.hide };
        }
        return column;
      })
    );
  }, []);

  const onShowAll = useCallback(() => {
    setLocalColumnState((currLocalColumnState) =>
      currLocalColumnState.map((column) =>
        column.lockPosition ? column : { ...column, hide: showAll }
      )
    );
  }, [showAll]);

  useEffect(() => {
    let isMounted = true;
    const currRef = gridRef.current;
    const setNewState = () => {
      if (!isMounted) return;
      setLocalColumnState(addAttributes(currRef.columnApi.getColumnState()));
    };
    currRef.api.addEventListener('columnMoved', setNewState);
    currRef.api.addEventListener('columnVisible', setNewState);
    return () => {
      isMounted = false;
      currRef.api.removeEventListener('columnMoved', setNewState);
      currRef.api.removeEventListener('columnVisible', setNewState);
    };
  }, [addAttributes, gridRef]);

  useEffect(() => {
    if (autoApplyChanges) {
      gridRef.current.columnApi.applyColumnState({ state: localColumnState, applyOrder: true });
    }
  }, [autoApplyChanges, gridRef, localColumnState]);

  return useMemo(
    () => ({
      onFilterChange,
      onShowAll,
      columns: filteredColumns,
      showUnpinAll,
      onApply,
      onCancel,
      onMove,
      onMoveEnd,
      onPin,
      onUnpinAll,
      onShow,
      filterValue,
      showAll,
    }),
    [
      filterValue,
      filteredColumns,
      onApply,
      onCancel,
      onFilterChange,
      showAll,
      onMove,
      onMoveEnd,
      onPin,
      onShow,
      onShowAll,
      onUnpinAll,
      showUnpinAll,
    ]
  );
};

const ColumnsAgGrid = ({ onClose, ...props }: IColumnsAgGridProps) => {
  const propsFromHook = useColumnsAgGrid({
    autoApplyChanges: props.autoApplyChanges,
    onClose,
    disablePinning: props.disablePinning,
  });
  // eslint-disable-next-line react/jsx-props-no-spreading
  return <Columns {...propsFromHook} {...props} />;
};

export default ColumnsAgGrid;
